import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { fetchList } from '@/lib/api';
import { MaskLine, SectionHead } from '@/components/Reveal';
import { RosterBlock } from '@/components/Roster';
import { useContent } from '@/lib/useContent';

const EASE = [0.16, 1, 0.3, 1];
const IS_TOUCH = typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0);
const slideVariants = {
  enter: (dir) => ({ x: dir >= 0 ? '14%' : '-14%', opacity: 0 }),
  center: { x: '0%', opacity: 1 },
  exit: (dir) => ({ x: dir >= 0 ? '-14%' : '14%', opacity: 0 }),
};

function HeroSlider({ campaigns }) {
  const content = useContent();
  const count = campaigns.length;
  const [[index, dir], setState] = useState([0, 0]);
  const touchX = useRef(null);

  const go = (d) => setState(([i]) => (count < 2 ? [i, 0] : [(i + d + count) % count, d]));
  const jump = (i) => setState(([cur]) => (i === cur ? [cur, 0] : [i, i > cur ? 1 : -1]));

  useEffect(() => {
    if (count < 2) return;
    const t = setInterval(() => go(1), 7000);
    return () => clearInterval(t);
  }, [index, count]);

  if (!count) {
    return (
      <section
        data-testid="hero-section"
        className="mt-[72px] flex h-[62svh] min-h-[430px] max-h-[620px] flex-col items-center justify-center border-b border-brayo-line bg-brayo-paper"
      >
        <img src="/brand/brayo-logo-alpha.png" alt="Brayo Records" className="h-20 md:h-28" />
        <p className="meta-label mt-8 text-brayo-dim">New music coming soon</p>
      </section>
    );
  }
  const hero = campaigns[index];

  return (
    <section
      data-testid="hero-section"
      className="relative mt-[72px] h-[62svh] min-h-[430px] max-h-[620px] overflow-hidden border-b border-brayo-line bg-brayo-paper"
      onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => {
        if (touchX.current == null) return;
        const dx = e.changedTouches[0].clientX - touchX.current;
        if (Math.abs(dx) > 50) go(dx < 0 ? 1 : -1);
        touchX.current = null;
      }}
    >
      <AnimatePresence initial={false} custom={dir} mode="popLayout">
        <motion.div
          key={hero.id || index}
          custom={dir}
          variants={slideVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{ duration: 0.8, ease: EASE }}
          className="absolute inset-0"
          data-testid="hero-slide"
        >
          <motion.img
            src={hero.image_url}
            alt={`${hero.artist_name} — ${hero.release_title}`}
            initial={{ scale: 1.06 }}
            animate={{ scale: 1 }}
            transition={{ duration: 6, ease: EASE }}
            className="absolute right-0 top-0 h-full w-full object-cover md:w-[58%]"
            style={{ objectPosition: 'center 25%' }}
          />
          <div className="absolute inset-y-0 right-0 w-full bg-gradient-to-r from-brayo-paper via-brayo-paper/70 to-transparent md:w-[62%] md:from-brayo-paper md:via-brayo-paper/25" />
        </motion.div>
      </AnimatePresence>

      <a
        data-testid="hero-slide-link"
        href={hero.listen_url}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`${hero.artist_name} — ${hero.release_title} — listen now`}
        className="absolute inset-0 z-[5] cursor-pointer"
      />

      <div className="pointer-events-none absolute inset-0 flex items-center">
        <div className="mx-auto w-full max-w-[1500px] px-5 md:px-10">
          <AnimatePresence initial={false} custom={dir} mode="popLayout">
            <motion.div
              key={`content-${hero.id || index}`}
              custom={dir}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.65, ease: EASE, delay: 0.06 }}
              className="max-w-xl md:max-w-2xl"
            >
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.5 }}
                className="meta-label mb-4 flex items-center gap-3 text-brayo-blue"
              >
                <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-brayo-blue" />
                {hero.kicker}
              </motion.p>

              <h1>
                <MaskLine delay={0.1}>
                  <span className="display-title inline-block bg-brayo-black px-4 py-1.5 text-3xl text-white sm:text-4xl md:px-6 md:py-2 md:text-6xl" style={{ boxShadow: '0 2px 0 0 #4FA8E8' }}>
                    {hero.artist_name}
                  </span>
                </MaskLine>
                <MaskLine delay={0.26}>
                  <span className="mt-4 block text-2xl font-bold text-brayo-charcoal sm:text-3xl md:text-4xl">
                    {hero.release_title}
                  </span>
                </MaskLine>
                <MaskLine delay={0.38}>
                  <span className="meta-label mt-3 inline-block text-brayo-blue">{hero.label}</span>
                </MaskLine>
              </h1>

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.7, duration: 0.6 }}
                className="meta-label mt-6 inline-block bg-brayo-paper/85 px-3.5 py-2 text-brayo-charcoal backdrop-blur-[2px]"
              >
                {content.hero_hint || (IS_TOUCH ? 'Tap anywhere to listen' : 'Click anywhere to listen')}
              </motion.p>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {count > 1 && (
        <div className="absolute bottom-4 left-1/2 z-10 flex -translate-x-1/2 gap-1">
          {campaigns.map((c, i) => (
            <button
              key={c.id || i}
              data-testid={`hero-dot-${i}`}
              aria-label={`Go to campaign ${i + 1}`}
              onClick={() => jump(i)}
              className="flex h-11 w-11 cursor-pointer items-center justify-center"
            >
              <span className={`h-1.5 rounded-full transition-all duration-500 ${i === index ? 'w-8 bg-brayo-blue' : 'w-1.5 bg-brayo-charcoal/30'}`} />
            </button>
          ))}
        </div>
      )}
    </section>
  );
}

export default function Home() {
  const content = useContent();
  const [hero, setHero] = useState([]);
  const [artists, setArtists] = useState([]);
  const [shopUrl, setShopUrl] = useState('');

  useEffect(() => {
    Promise.all([
      fetchList('/hero'),
      fetchList('/artists'),
      fetchList('/settings'),
    ]).then(([h, a, s]) => {
      setHero(Array.isArray(h) ? h : h ? [h] : []);
      setArtists(a);
      setShopUrl(s?.shop_url || '');
    }).catch(() => {});
  }, []);

  return (
    <>
      <HeroSlider campaigns={hero} />

      {/* ROSTER */}
      <section data-testid="home-artists-section" className="mx-auto max-w-[1200px] px-5 py-20 md:px-10 md:py-28">
        <SectionHead
          overline={content.home_roster_overline || 'The Roster'}
          title={content.home_roster_title || 'Artists'}
          link="/artists"
          linkLabel="All Artists"
        />
        <RosterBlock artists={artists} />
      </section>

      {/* STORE */}
      <section data-testid="home-shop-section" className="border-t border-brayo-line bg-brayo-panel/60">
        <div className="mx-auto flex max-w-[1500px] flex-col items-start gap-8 px-5 py-20 md:flex-row md:items-center md:justify-between md:px-10 md:py-28">
          <div>
            <p className="meta-label mb-4 text-brayo-blue">{content.home_store_overline || 'Official Store'}</p>
            <h2 className="display-title text-4xl text-brayo-charcoal sm:text-5xl lg:text-6xl">
              {content.home_store_title || 'Merch & Vinyl'}
            </h2>
          </div>
          <a
            data-testid="home-shop-btn"
            href={shopUrl || '/shop'}
            target={shopUrl ? '_blank' : undefined}
            rel={shopUrl ? 'noopener noreferrer' : undefined}
            className="arrow-slide meta-label flex items-center gap-3 bg-brayo-blue px-8 py-4 text-white transition-colors hover:bg-brayo-charcoal"
          >
            {content.home_store_btn || 'Visit The Store'}
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M7 17L17 7M9 7h8v8" /></svg>
          </a>
        </div>
      </section>
    </>
  );
}